import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from './cn';

/*
  Input — текстовое поле с опциональными иконками, label, helper-текстом и состоянием ошибки.
  Высота md (36) — как у Button md, чтобы стоять в одной строке (R11).
  Toggle/checkbox/radio — отдельные примитивы (фаза 3).
*/

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  helper?: string;
  error?: string;
  /** Opt-in stronger error text; existing consumers keep their color. */
  errorTone?: 'default' | 'strong';
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  fullWidth?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      helper,
      error,
      errorTone = 'default',
      leadingIcon,
      trailingIcon,
      fullWidth,
      className,
      id,
      ...rest
    },
    ref,
  ) => {
    const reactId = useId();
    const inputId = id ?? `input-${reactId}`;
    const helpId = `${inputId}-help`;
    return (
      <div data-slot="field" className={cn('flex flex-col gap-1.5', fullWidth && 'w-full')}>
        {label && (
          <label htmlFor={inputId} className="text-xs font-medium text-fg-muted">
            {label}
          </label>
        )}
        <div className="relative">
          {leadingIcon && (
            <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-fg-subtle [&_svg]:size-4">
              {leadingIcon}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            data-slot="input"
            className={cn(
              'h-9 w-full rounded-md border bg-card px-3 text-sm text-fg placeholder:text-fg-subtle',
              'transition-colors duration-fast',
              'focus-visible:ring-2 focus-visible:outline-none',
              'disabled:cursor-not-allowed disabled:opacity-50',
              error
                ? 'border-danger focus-visible:border-danger focus-visible:ring-danger/40'
                : 'border-line hover:border-line-strong focus-visible:border-ring focus-visible:ring-ring/40',
              leadingIcon && 'pl-9',
              trailingIcon && 'pr-9',
              className,
            )}
            aria-invalid={!!error}
            aria-describedby={helper || error ? helpId : undefined}
            {...rest}
          />
          {trailingIcon && (
            <span className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-fg-subtle [&_svg]:size-4">
              {trailingIcon}
            </span>
          )}
        </div>
        {(helper || error) && (
          <p
            id={helpId}
            className={cn(
              'text-xs',
              error ? (errorTone === 'strong' ? 'text-danger-strong' : 'text-danger-fg') : 'text-fg-subtle',
            )}
          >
            {error || helper}
          </p>
        )}
      </div>
    );
  },
);
Input.displayName = 'Input';
